import { KpiCard } from '../../../components/KpiCard';
import { Badge, Icon, LinkButton, ProgressBar, SectionCard } from '../../../components/ui';
import { formatBRLFromCents, formatCount, formatRatio } from '../../../lib/format';
import { razaoBps, type DesempenhoDoModelo } from '../comparar';
import type { MessageTemplateSummary } from '../types';
import { SEGMENT_FAMILY_LABEL, TEMPLATE_APPROVAL_LABEL, TEMPLATE_APPROVAL_TONE, TEMPLATE_CATEGORY_LABEL, TEMPLATE_CATEGORY_TONE, TEMPLATE_SECTOR_LABEL } from '../vocabulario';

/**
 * Aba "Modelos" da tela de WhatsApp: resumo e RANKING por desempenho.
 *
 * Criar, editar e mandar para aprovacao e do modulo `admin-modelos`; aqui a
 * pessoa confere o que existe, quantos estao prontos para a API oficial e,
 * principalmente, QUAL TEXTO FAZ PAGAR: o ranking ordena pela taxa de
 * pagamento de quem recebeu, com o recuperado ao lado. Modelo sem envio no
 * periodo vai para o fim com "sem envio", nunca com "0%" -- zero de nada nao
 * e uma afirmacao sobre o texto.
 */
export function ResumoDeModelos({ templates, desempenho }: { readonly templates: readonly MessageTemplateSummary[]; readonly desempenho: ReadonlyMap<string, DesempenhoDoModelo> }): JSX.Element {
  const aprovados = templates.filter((t) => t.approval === 'aprovado').length;
  const emAnalise = templates.filter((t) => t.approval === 'em_analise').length;
  const textoLivre = templates.filter((t) => t.approval === 'texto_livre').length;
  const marketing = templates.filter((t) => t.category === 'marketing').length;

  const taxa = (t: MessageTemplateSummary): number | null => {
    const d = desempenho.get(t.id);
    return d === undefined ? null : razaoBps(d.pagaram, d.entregues);
  };
  const ranking = [...templates].sort((a, b) => {
    const ta = taxa(a);
    const tb = taxa(b);
    if (ta === null && tb === null) {
      return 0;
    }
    if (ta === null) {
      return 1;
    }
    if (tb === null) {
      return -1;
    }
    return tb - ta;
  });
  const melhor = ranking.map(taxa).find((v): v is number => v !== null) ?? 0;

  return (
    <div className="flex flex-col gap-6">
      <div className="grid gap-4 sm:grid-cols-3">
        <KpiCard label="Modelos de WhatsApp" value={formatCount(templates.length)} support={`${formatCount(marketing)} marketing · ${formatCount(templates.length - marketing)} utility`} icon={<Icon name="modelo" />} />
        <KpiCard label="Aprovados na Meta" value={formatCount(aprovados)} support={emAnalise === 0 ? 'prontos para a API oficial' : `${formatCount(emAnalise)} em análise`} icon={<Icon name="check" />} />
        <KpiCard label="Texto livre" value={formatCount(textoLivre)} support="saem só pelo número do comercial (Evolution)" icon={<Icon name="whatsapp" />} />
      </div>

      <SectionCard
        id="modelos-ranking"
        icon="estrela"
        title="Qual texto faz pagar"
        description="Ranking pela taxa de pagamento de quem recebeu, em todos os envios do período. Criação, edição e aprovação ficam na tela de Modelos."
        aside={
          <LinkButton to="/admin/modelos" variant="primary" icon="modelo" iconRight="seta-dir">
            Gerenciar modelos
          </LinkButton>
        }
        padded={false}
      >
        <ol className="divide-y divide-line">
          {ranking.map((t, i) => {
            const d = desempenho.get(t.id);
            const bps = taxa(t);
            return (
              <li key={t.id} className="flex flex-col gap-2 px-5 py-3 md:flex-row md:items-center md:gap-5">
                <div className="flex min-w-0 flex-1 items-start gap-3">
                  <span className="mt-0.5 w-6 shrink-0 text-right text-sm font-semibold tabular-nums text-ink-subtle">{i + 1}.</span>
                  <div className="min-w-0">
                    <p className="font-medium text-ink">{t.name}</p>
                    <p className="mt-0.5 line-clamp-1 text-sm text-ink-muted">{t.body}</p>
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      <Badge tone={TEMPLATE_CATEGORY_TONE[t.category]}>{TEMPLATE_CATEGORY_LABEL[t.category]}</Badge>
                      <Badge tone="neutral">{TEMPLATE_SECTOR_LABEL[t.sector]}</Badge>
                      <Badge tone={TEMPLATE_APPROVAL_TONE[t.approval]} dot>
                        {TEMPLATE_APPROVAL_LABEL[t.approval]}
                      </Badge>
                      <span className="text-xs text-ink-subtle">para {t.fits.map((f) => SEGMENT_FAMILY_LABEL[f].toLowerCase()).join(' e ')}</span>
                    </div>
                  </div>
                </div>
                <div className="flex w-full shrink-0 flex-col gap-1 md:w-64">
                  {d === undefined || bps === null ? (
                    <p className="text-xs text-ink-subtle">Sem envio no período</p>
                  ) : (
                    <>
                      <p className="flex items-baseline justify-between gap-2 text-sm">
                        <span className="font-semibold tabular-nums text-ink">{formatRatio(d.pagaram, d.entregues)}</span>
                        <span className="text-xs text-ink-subtle">{formatBRLFromCents(d.recuperadoCents)}</span>
                      </p>
                      <ProgressBar value={bps} max={Math.max(1, melhor)} tone="good" label={`Taxa de pagamento de ${t.name}`} />
                      <p className="text-xs text-ink-subtle">
                        {formatCount(d.pagaram)} pagaram de {formatCount(d.entregues)} que receberam · {formatCount(d.envios)} {d.envios === 1 ? 'envio' : 'envios'}
                      </p>
                    </>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      </SectionCard>
    </div>
  );
}
